import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isLawyer } from '@/utils/session';
import { requestPushPermissionAndGetToken } from '@/utils/pushNotifications';
import { registerPushToken } from '@/utils/requests/notifications';
import { sendEvent } from '@/utils/requests/user_events';
import { Button } from '@/components/ui/button';

const DISMISSED_KEY = 'push_banner_dismissed_at';
const REGISTERED_KEY = 'last_check_push_token';
const DISMISS_INTERVAL = 14 * 24 * 60 * 60 * 1000; // 14 días si eligió "Ahora no"
const REGISTRATION_DELAY = 5 * 24 * 60 * 60; // 5 días en segundos (created_at es unix en segundos)

function shouldShowBanner(): boolean {
    if (!('Notification' in window)) return false;
    if (Notification.permission !== 'default') return false;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!isLawyer(user)) return false;
    if (localStorage.getItem(REGISTERED_KEY)) return false;
    // Solo mostrar si el abogado se registró hace más de 5 días
    if (!user.created_at || Date.now() / 1000 - user.created_at < REGISTRATION_DELAY) return false;
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_INTERVAL) return false;
    return true;
}

export function PushNotificationBanner() {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const isAllowedRoute =
            location.pathname === '/lawyer/marketplace' ||
            location.pathname.startsWith('/lawyer/cases/');
        const show = isAllowedRoute && shouldShowBanner();
        setVisible(show);
        if (show) sendEvent('push_banner_shown', 0);
    }, [location.pathname]);

    if (!visible) return null;

    const handleDismiss = () => {
        sendEvent('push_banner_dismiss', 0);
        localStorage.setItem(DISMISSED_KEY, Date.now().toString());
        setVisible(false);
    };

    const handleActivate = async () => {
        sendEvent('push_banner_activate', 0);
        setLoading(true);
        setError(false);
        try {
            const token = await requestPushPermissionAndGetToken();
            if (token) {
                const ok = await registerPushToken(0, token);
                if (ok) {
                    localStorage.setItem(REGISTERED_KEY, Date.now().toString());
                    setVisible(false);
                    return;
                }
            }
            // Si permission quedó denied, no tiene sentido mostrar el banner más
            if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
                setVisible(false);
                return;
            }
            // Caso: permission granted pero token falló (VAPID key, red, etc.)
            setError(true);
        } catch (err) {
            console.error('[Push] Error activating push notifications:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl bg-white dark:bg-neutral-900 shadow-xl overflow-hidden border border-violet-100 dark:border-violet-900/40">
            <div className="h-1 w-full bg-gradient-to-r from-violet-600 to-purple-600" />
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                        <span className="text-lg leading-none">🔔</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-900 dark:text-neutral-100">
                            ¡No te pierdas ningún caso nuevo!
                        </p>
                        <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">
                            Recibí una alerta al instante cuando llegue un caso a tu zona, sin esperar el email.
                        </p>
                    </div>
                </div>
                {error && (
                    <p className="text-xs text-red-500 mt-2">
                        No se pudo activar. Verificá el candado en la barra de URL.
                    </p>
                )}
                <div className="flex gap-2 mt-3 justify-end">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDismiss}
                        disabled={loading}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                    >
                        Ahora no
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleActivate}
                        disabled={loading}
                        className="text-xs bg-violet-600 hover:bg-violet-700 text-white"
                    >
                        {loading ? 'Activando...' : 'Activar'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
